import React, { useId, useMemo, useState } from "react";
import { compiler } from "markdown-to-jsx";
import { ChevronDown } from "lucide-react";
import MarkdownText from "../common/MarkdownText";

const BLOCKS = new Set([
  "p",
  "div",
  "li",
  "ul",
  "ol",
  "blockquote",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
]);

// Extract visible words before cutting the preview, so a Markdown link or
// emphasis marker can never be cut in half. The full copy keeps its formatting.
function visibleText(node) {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(visibleText).join("");
  if (node.type === "br") return " ";
  if (node.type === "img") return node.props.alt || "";
  const text = visibleText(node.props?.children);
  return BLOCKS.has(node.type) ? ` ${text} ` : text;
}

function excerpt(description) {
  if (typeof description !== "string" || !description.trim())
    return { preview: "", canExpand: false };
  const words = visibleText(compiler(description, { disableParsingRawHTML: true }))
    .trim()
    .split(/\s+/u)
    .filter(Boolean);
  if (words.length <= 12) return { preview: words.join(" "), canExpand: false };
  return {
    preview: words.slice(0, Math.ceil(words.length / 4)).join(" ") + "…",
    canExpand: true,
  };
}

export default function PastEventDescription({
  description,
  attendees,
  coordinator,
  specialMention,
  detailsAction,
}) {
  const { preview, canExpand } = useMemo(() => excerpt(description), [description]);
  const [expanded, setExpanded] = useState(false);
  const contentId = useId();
  const hasDescription = typeof description === "string" && description.trim();
  const hasCredits = Boolean(coordinator || specialMention);
  const needsToggle = canExpand || hasCredits || Boolean(detailsAction);
  if (!hasDescription && !hasCredits && !detailsAction && attendees == null)
    return null;
  return (
    <>
      <MarkdownText className="smym-past-description-desktop mb-3 text-sm text-[var(--smym-ink-muted)]">
        {description}
      </MarkdownText>
      <div className="smym-past-description-mobile mb-3 text-sm text-[var(--smym-ink-muted)]">
        <div id={contentId}>
          {hasDescription &&
            (expanded || !canExpand ? (
              <MarkdownText>{description}</MarkdownText>
            ) : (
              <p className="leading-relaxed">{preview}</p>
            ))}
          {expanded && hasCredits && (
            <div className="smym-past-expanded-credits">
              {coordinator && <p>Coordinator: {coordinator}</p>}
              {specialMention && (
                <div className="text-[var(--smym-accent)]">
                  Special Mention: <MarkdownText inline>{specialMention}</MarkdownText>
                </div>
              )}
            </div>
          )}
          {expanded && detailsAction}
        </div>
        {(attendees != null || needsToggle) && (
          <div className="smym-past-description-row">
            {attendees != null && (
              <span className="smym-past-participants">{attendees} Participants</span>
            )}
            {needsToggle && (
              <button
                type="button"
                className="smym-past-description-toggle"
                aria-expanded={expanded}
                aria-controls={contentId}
                onClick={() => setExpanded((value) => !value)}
              >
                {expanded ? "Less info" : "More info"}
                <ChevronDown
                  className={`h-3.5 w-3.5${expanded ? " rotate-180" : ""}`}
                  aria-hidden="true"
                />
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
}
